// src/data/worksheets/awareness.js — performance-side pair (authored).
// Built off the Self-Awareness action plan: two-minute check-in, spot the pattern, ask one person.

export const AWARENESS_CHECKLIST = {
    id: 'awareness_checklist', topicId: 'self-awareness', kind: 'checklist',
    title: 'Self-Awareness Checklist',
    intro: 'Notice how you\'re doing before it shows up in your results. Tick these off as you build them in.',
    sections: [
        { id: 'checkin', title: 'The Daily Check-In', prompts: [
            { id: 'twomin', type: 'checklist', label: 'Two-Minute Check-In', items: [
                { id: 't1', label: 'Once a day, rate energy, mood and stress — each out of ten.' },
                { id: 't2', label: 'Do it at the same time each day so the numbers mean something.' }
            ] },
            { id: 'body', type: 'checklist', label: 'Read the Body', items: [
                { id: 'b1', label: 'Notice where tension sits (jaw, shoulders, stomach) — it is usually the first signal.' },
                { id: 'b2', label: 'Check breathing: short and high means the stress number is higher than you think.' }
            ] }
        ] },
        { id: 'patterns', title: 'Spot the Pattern', prompts: [
            { id: 'lookback', type: 'checklist', label: 'Look Back Weekly', items: [
                { id: 'l1', label: 'After a week, look for what tends to come before your good and bad days.' },
                { id: 'l2', label: 'Name one trigger and one booster you can now see coming.' }
            ] },
            { id: 'tells', type: 'checklist', label: 'Know Your Tells', items: [
                { id: 'te1', label: 'Write down how you behave when you are under pressure (quiet, sharp, rushed, joking).' },
                { id: 'te2', label: 'Write down how you behave when you are at your best — so you can tell the difference in the moment.' }
            ] }
        ] },
        { id: 'outside', title: 'Get an Outside View', prompts: [
            { id: 'ask', type: 'checklist', label: 'Ask One Person', items: [
                { id: 'a1', label: 'Ask someone you trust what they notice when you are under pressure.' },
                { id: 'a2', label: 'Listen without defending. Write down what they said.' }
            ] },
            { id: 'feedback', type: 'checklist', label: 'Invite Feedback', items: [
                { id: 'f1', label: 'After a key moment, ask: "what did you see?" before you say what you felt.' },
                { id: 'f2', label: 'Look for the gap between how it felt and how it looked.' }
            ] }
        ] },
        { id: 'values', title: 'Know What Drives You', prompts: [
            { id: 'values', type: 'checklist', label: 'Values and Strengths', items: [
                { id: 'v1', label: 'Write your top three values — what has to be true for you to feel right.' },
                { id: 'v2', label: 'Write your top three strengths and one situation where each shows up.' }
            ] },
            { id: 'reactions', type: 'checklist', label: 'Own Your Reactions', items: [
                { id: 'r1', label: 'When you react strongly, pause and ask what it was really about.' },
                { id: 'r2', label: 'Name the emotion specifically (frustrated, embarrassed, worried) — precision calms it.' }
            ] }
        ] },
        { id: 'review', title: 'Review & Reflect', prompts: [
            { id: 'weekly', type: 'checklist', label: 'Weekly', items: [
                { id: 'wk1', label: 'Look at the week\'s check-ins. What is the pattern?' },
                { id: 'wk2', label: 'Decide one thing to do differently because of what you noticed.' }
            ] },
            { id: 'notes', type: 'text', label: 'Notes', multiline: true }
        ] }
    ],
    outro: 'You cannot manage what you do not notice. Two minutes a day is enough to start noticing.'
};

export const AWARENESS_WORKSHEET = {
    id: 'awareness_worksheet', topicId: 'self-awareness', kind: 'worksheet',
    title: 'Self-Awareness Worksheet',
    intro: 'Answer in your own words. Nothing here is scored, and only you can see it.',
    sections: [
        { id: 'self', title: 'Self Assessment', prompts: [
            { id: 'level', type: 'scale', label: 'How well do you notice how you\'re doing before it affects your performance?', min: 1, max: 10, minLabel: 'Blindsided', maxLabel: 'See it coming' },
            { id: 'tells', type: 'list', label: 'Your tells under pressure', help: 'How you behave when the pressure is on (e.g., go quiet, get sharp with people, rush, over-joke).', count: 3 },
            { id: 'best', type: 'list', label: 'Your signs at your best', help: 'How you know you\'re in a good place.', count: 3 }
        ] },
        { id: 'understanding', title: 'Understanding Yourself', prompts: [
            { id: 'values', type: 'list', label: 'Top three values', help: 'What has to be true for you to feel right about how you\'re living and working.', count: 3 },
            { id: 'strengths', type: 'list', label: 'Top three strengths — and where each shows up', count: 3 },
            { id: 'triggers', type: 'list', label: 'What tends to come before a bad day', help: 'Look back at the last few weeks.', count: 3 },
            { id: 'boosters', type: 'list', label: 'What tends to come before a good day', count: 3 }
        ] },
        { id: 'outside', title: 'The Outside View', prompts: [
            { id: 'asked', type: 'text', label: 'What one person you trust notices about you under pressure', help: 'Ask them. Write it without defending.', multiline: true },
            { id: 'gap', type: 'text', label: 'The gap between how it feels and how it looks', help: 'Where their view and yours differ.', multiline: true }
        ] },
        { id: 'plan', title: 'Action Plan', prompts: [
            { id: 'checkintime', type: 'text', label: 'When you will do your two-minute check-in each day' },
            { id: 'change', type: 'text', label: 'One thing you will do differently because of what you\'ve noticed', multiline: true },
            { id: 'person', type: 'text', label: 'Who you will ask for an outside view this week' }
        ] },
        { id: 'track', title: 'Track It', description: 'Energy, mood, stress — each out of ten, once a day. After a week the pattern shows itself.', prompts: [
            { id: 'tracker', type: 'table', label: 'Daily check-in', columns: [
                { id: 'day', label: 'Day', kind: 'date' },
                { id: 'energy', label: 'Energy (1–10)', kind: 'number', min: 1, max: 10 },
                { id: 'mood', label: 'Mood (1–10)', kind: 'number', min: 1, max: 10 },
                { id: 'stress', label: 'Stress (1–10)', kind: 'number', min: 1, max: 10 },
                { id: 'before', label: 'What came before', kind: 'text' }
            ], maxRows: 31, chart: { x: 'day', series: [{ id: 'energy', label: 'Energy' }, { id: 'mood', label: 'Mood' }, { id: 'stress', label: 'Stress' }] } }
        ] }
    ],
    outro: 'Awareness is the skill underneath every other skill. Keep the check-ins going.'
};

export const AWARENESS_WORKSHEETS = [AWARENESS_CHECKLIST, AWARENESS_WORKSHEET];
